import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@startengine/design-system.ui.button';
import { Modal } from '@startengine/design-system.ui.modal';
import useIsMobile from 'hooks/useIsMobile';
import { imagePath } from 'utils/helpers';
import { trackEvent } from 'utils/analytics';

const SPARK_FUND_OFFERING_URL = 'https://www.startengine.com/offering/spark-fund-2';

const POPUP_EVENTS = {
  shown: 'private_popup_shown',
  dismissed: 'private_popup_dismissed',
  ctaClicked: 'private_popup_cta_clicked',
};

const VARIANT_COPY = {
  closed: {
    titlePrefix: 'This Offering is Closed, But ',
    titleSuffix: ' Just Launched',
  },
  'explore-cta': {
    titlePrefix: null,
    titleSuffix: ' Just Launched',
  },
};

const safeReadStorage = (storageKey) => {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.sessionStorage.getItem(storageKey));
  } catch {
    return false;
  }
};

const safeWriteStorage = (storageKey) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(storageKey, 'true');
  } catch {}
};

const getScrollPercent = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
  const scrollHeight = Math.max(
    doc.scrollHeight || 0,
    body.scrollHeight || 0,
    doc.offsetHeight || 0,
    body.offsetHeight || 0,
  );
  const viewportHeight = window.innerHeight || doc.clientHeight || 0;
  const scrollable = scrollHeight - viewportHeight;
  if (scrollable <= 0) return 0;
  return Math.min(100, Math.max(0, (scrollTop / scrollable) * 100));
};

const PrivateOfferingPopup = ({
  variant = 'closed',
  utmContent = 'own-page-popup',
  storageKey = 'privateOfferingPopupDismissed',
  scrollPercent,
  delayMs,
  enabled = true,
}) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const copy = VARIANT_COPY[variant] || VARIANT_COPY.closed;
  const targetUrl = `${SPARK_FUND_OFFERING_URL}?utm_content=${encodeURIComponent(utmContent)}`;

  useEffect(() => {
    if (!enabled) return undefined;
    if (safeReadStorage(storageKey)) return undefined;

    let triggered = false;
    let timerId;
    let rafId = null;
    let scrollHandler;

    const cleanupTriggers = () => {
      if (timerId) {
        clearTimeout(timerId);
        timerId = undefined;
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = undefined;
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const fire = (trigger) => {
      if (triggered) return;
      triggered = true;
      cleanupTriggers();
      setOpen(true);
      trackEvent(POPUP_EVENTS.shown, { variant, utmContent, trigger });
    };

    if (typeof delayMs === 'number' && delayMs >= 0) {
      timerId = setTimeout(() => fire('delay'), delayMs);
    }

    if (typeof scrollPercent === 'number' && scrollPercent > 0) {
      scrollHandler = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (getScrollPercent() >= scrollPercent) {
            fire('scroll');
          }
        });
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
      scrollHandler();
    }

    return cleanupTriggers;
  }, [enabled, storageKey, scrollPercent, delayMs, variant, utmContent]);

  const handleClose = () => {
    setOpen(false);
    safeWriteStorage(storageKey);
    trackEvent(POPUP_EVENTS.dismissed, { variant, utmContent });
  };

  const handleCtaClick = () => {
    trackEvent(POPUP_EVENTS.ctaClicked, { variant, utmContent });
    setOpen(false);
    safeWriteStorage(storageKey);
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  if (!enabled) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      bottom={isMobile}
      hideHeader
      backgroundColor="transparent"
      data-cy="private-offering-popup"
      data-cy-variant={variant}
      data-block-recording
      sx={{
        '.modal-container': {
          width: isMobile ? '100%' : '540px',
          maxWidth: isMobile ? '100%' : '540px !important',
          borderRadius: isMobile ? '32px 32px 0 0' : '32px !important',
          overflow: 'hidden',
          boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
          backgroundColor: 'transparent !important',
          background: 'transparent !important',
          padding: '0 !important',
          margin: '0 !important',
        },
        '.modal-body': {
          overflow: 'hidden',
          padding: '0 !important',
          margin: '0 !important',
          backgroundColor: 'transparent !important',
          background: 'transparent !important',
        },
        '.modal-footer': {
          display: 'none !important',
        },
        '.MuiGrid-container': {
          padding: '0 !important',
          margin: '0 !important',
          background: 'transparent !important',
        },
        '.MuiGrid-item': {
          padding: '0 !important',
        },
      }}
    >
      <Box
        sx={{
          background: `url(${imagePath}/images/offering/modal_bg.png) center / cover no-repeat, linear-gradient(160deg, rgba(60,80,60,0.95) 0%, rgba(40,60,50,0.97) 40%, rgba(30,45,40,0.98) 100%)`,
          borderRadius: isMobile ? '32px 32px 0 0' : '32px',
          padding: isMobile ? '32px 20px 20px' : '48px 40px 28px',
          position: 'relative',
          color: '#FFFFFF',
        }}
      >
        <IconButton
          onClick={handleClose}
          aria-label="Close popup"
          data-cy="private-offering-popup-close"
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography
          component="h2"
          sx={{
            color: '#FFFFFF',
            fontFamily: '"owners-text", Georgia, serif',
            fontWeight: 400,
            fontSize: isMobile ? '28px' : '40px',
            lineHeight: 1.15,
            pr: 4,
          }}
        >
          {copy.titlePrefix}
          <Box component="span" sx={{ fontStyle: 'italic' }}>
            Spark Fund 2
          </Box>
          {copy.titleSuffix}
        </Typography>

        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            fontFamily: '"owners-text", Georgia, serif',
            fontSize: isMobile ? '15px' : '17px',
            lineHeight: 1.4,
            mt: 2.5,
          }}
        >
          Spark Fund 2 has a lower minimum compared to some of our other multi-offering Funds.
        </Typography>

        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            fontFamily: '"owners-text", Georgia, serif',
            fontSize: isMobile ? '15px' : '17px',
            lineHeight: 1.4,
            mt: 2,
            mb: 4,
          }}
        >
          With one check, you can gain exposure to OpenAI, PsiQuantum, Ripple, and more.
        </Typography>

        <Button
          variant="contained"
          onClick={handleCtaClick}
          data-cy="private-offering-popup-cta"
          sx={{
            backgroundColor: '#e8843c',
            color: '#FFFFFF',
            fontWeight: 700,
            padding: '14px 28px',
            fontSize: '17px',
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#d4742f',
            },
          }}
        >
          View Spark Fund 2
        </Button>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#DDDDDD',
            mt: isMobile ? 3 : 4,
            fontSize: '10px',
            lineHeight: 1.5,
          }}
        >
          Offerings on StartEngine Private are made under Regulation D, Rule 506(c), through
          StartEngine Primary LLC (member FINRA/SIPC), and are available only to accredited
          investors. Investments are in Series 20-1 (&ldquo;Spark Fund 2&rdquo;), which may hold
          shares of multiple portfolio companies directly or through an SPV. Available only to
          accredited investors. You are not buying stock in any portfolio company directly. Revenue,
          funding, or valuation may not increase, and profitability is not assured. These securities
          are illiquid and may trade at lower prices. Valuations are based on private placements
          rather than public markets. See the offering page for more information, and to review risk
          disclosures.
        </Typography>
      </Box>
    </Modal>
  );
};

export default PrivateOfferingPopup;
